"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  IconButton,
  Divider,
  Chip,
  AppBar,
  Toolbar,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  CircularProgress,
} from "@mui/material"
import { CloudUpload, Description, Close, Download, Preview, Delete, Upload, FileUpload } from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import * as Papa from "papaparse"
import { uploadAllowancesAndDedcution, getPayrollItems } from "../../../api/payroll/payrolls"
import { requestID } from "../../../common/utils"
import { IonPage } from "@ionic/react"

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
})

const months = [
  { value: "01", name: "January" },
  { value: "02", name: "February" },
  { value: "03", name: "March" },
  { value: "04", name: "April" },
  { value: "05", name: "May" },
  { value: "06", name: "June" },
  { value: "07", name: "July" },
  { value: "08", name: "August" },
  { value: "09", name: "September" },
  { value: "10", name: "October" },
  { value: "11", name: "November" },
  { value: "12", name: "December" },
]

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 10 }, (_, i) => currentYear - i)

interface AllowanceDeduction {
  id: string
  name: string
  type: "allowance" | "deduction"
  created_at: string
  branch_id: string
  business_id: string
}

const EmployeeUploadPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString())
  const [selectedMonth, setSelectedMonth] = useState<string>("")
  const [selectedType, setSelectedType] = useState<"allowance" | "deduction">("allowance")
  const [selectedItem, setSelectedItem] = useState<string>("")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false)
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [parsedData, setParsedData] = useState<any[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [items, setItems] = useState<AllowanceDeduction[]>([])
  const [loadingItems, setLoadingItems] = useState<boolean>(true)

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoadingItems(true)
        const data = {
          business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
          branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
        }
        const response = await getPayrollItems(data)
        console.log(response)
        setItems(response.data.data)
      } catch (error) {
        setErrorMessage("Failed to load allowance/deduction items")
        setShowErrorAlert(true)
      } finally {
        setLoadingItems(false)
      }
    }
    fetchItems()
  }, [])

  const filteredItems = items.filter((item) => item.type === selectedType)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0]
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setSelectedFile(file)
        parseCSV(file)
      } else {
        setErrorMessage("Please upload a valid CSV file")
        setShowErrorAlert(true)
      }
    }
  }

  const parseCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        if (results.data.length > 0) {
          setHeaders(results.meta.fields || [])
          setParsedData(results.data.slice(0, 5)) // Show first 5 rows for preview
        }
      },
      error: (error) => {
        setErrorMessage("Error parsing CSV file: " + error.message)
        setShowErrorAlert(true)
      },
    })
  }

  const handlePreview = () => {
    if (!selectedFile) {
      setErrorMessage("No file selected for preview")
      setShowErrorAlert(true)
      return
    }
    setShowPreview(true)
  }

  const handleDownloadSample = () => {
    const sampleCSV = `Employee ID,Amount,Remarks\nEMP001,500,Transport allowance\nEMP002,300,Meal allowance\nEMP003,450,Performance bonus`
    const blob = new Blob([sampleCSV], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "employee_upload_sample.csv"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleUpload = async () => {
    if (!selectedYear || !selectedMonth) {
      setErrorMessage("Please select both year and month")
      setShowErrorAlert(true)
      return
    }
    if (!selectedItem) {
      setErrorMessage("Please select an allowance/deduction type")
      setShowErrorAlert(true)
      return
    }
    if (!selectedFile) {
      setErrorMessage("Please select a file to upload")
      setShowErrorAlert(true)
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    const formData = new FormData()
    formData.append("csv_file", selectedFile)
    formData.append("year", selectedYear)
    formData.append("month", selectedMonth)
    formData.append("business_id", "bus_id_b96c58e8adabb47c000dc8dc5c02900f")
    formData.append("branch_id", "bra_id_b96c58e8adabb47c038dc8dc5c00009m")
    formData.append("requestID", requestID())
    formData.append("type", selectedType)
    formData.append("item_id", selectedItem)

    try {
      const response = await uploadAllowancesAndDedcution(formData, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))
        setUploadProgress(progress)
      })
      console.log({ response })
      setIsUploading(false)
      setShowSuccessAlert(true)
      resetForm()
    } catch (error: any) {
      console.error(error)
      setIsUploading(false)
      setErrorMessage(error.message || "Failed to upload file")
      setShowErrorAlert(true)
    }
  }

  const resetForm = () => {
    setSelectedFile(null)
    setParsedData([])
    setHeaders([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const selectedItemName = selectedItem ? items.find((i) => i.id === selectedItem)?.name : ""

  return (
    <IonPage className="pushDown">
   <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: "primary.main" }}>
        <Toolbar>
          <Avatar sx={{ mr: 2, bgcolor: "primary.dark" }}>
            <FileUpload />
          </Avatar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Upload Allowances & Deductions
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Period Selection */}
        <Fade in timeout={300}>
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardHeader
              title="Select Period"
              subheader="Choose the year and month for the upload"
              sx={{ bgcolor: "grey.50" }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Year</InputLabel>
                    <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
                      {years.map((year) => (
                        <MenuItem key={year} value={year.toString()}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>

        {/* Type Selection */}
        <Fade in timeout={400}>
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardHeader
              title="Select Type & Item"
              subheader="Choose whether to upload allowances or deductions"
              sx={{ bgcolor: "grey.50" }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={selectedType}
                      label="Type"
                      onChange={(e) => {
                        setSelectedType(e.target.value as "allowance" | "deduction")
                        setSelectedItem("")
                      }}
                    >
                      <MenuItem value="allowance">Allowance</MenuItem>
                      <MenuItem value="deduction">Deduction</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={loadingItems || filteredItems.length === 0}>
                    <InputLabel>{selectedType === "allowance" ? "Allowance" : "Deduction"}</InputLabel>
                    <Select value={selectedItem} label={selectedType} onChange={(e) => setSelectedItem(e.target.value)}>
                      {loadingItems ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading...
                        </MenuItem>
                      ) : filteredItems.length === 0 ? (
                        <MenuItem disabled>No {selectedType}s found</MenuItem>
                      ) : (
                        filteredItems.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>

        {/* File Upload */}
        <Fade in timeout={500}>
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardHeader
              title="Upload CSV File"
              subheader={`Upload employee ${selectedItemName || selectedType} data in CSV format`}
              sx={{ bgcolor: "grey.50" }}
            />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please upload a CSV file containing employee data. The file should include columns for Employee ID,
                Amount, and Remarks.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Button component="label" variant="contained" startIcon={<CloudUpload />} size="large">
                  Select CSV File
                  <VisuallyHiddenInput type="file" accept=".csv" onChange={handleFileChange} ref={fileInputRef} />
                </Button>
                <Button variant="outlined" onClick={handleDownloadSample} startIcon={<Download />} size="large">
                  Download Sample
                </Button>
              </Box>

              {selectedFile && (
                <Paper sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Description sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {selectedFile.name}
                    </Typography>
                    <Chip label={`${Math.round(selectedFile.size / 1024)} KB`} size="small" />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" size="small" onClick={handlePreview} startIcon={<Preview />}>
                      Preview Data
                    </Button>
                    <Button variant="text" color="error" size="small" onClick={resetForm} startIcon={<Delete />}>
                      Remove File
                    </Button>
                  </Box>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Fade>

        {/* Upload Progress */}
        {isUploading && (
          <Fade in timeout={300}>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={24} />
                  Uploading File...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                  {uploadProgress}% Complete
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={resetForm} disabled={isUploading} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !selectedMonth || !selectedItem}
            startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
            size="large"
          >
            {isUploading ? "Uploading..." : "Upload Data"}
          </Button>
        </Box>
      </Container>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">CSV Preview (First 5 Rows)</Typography>
            <IconButton onClick={() => setShowPreview(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header} sx={{ fontWeight: "bold", bgcolor: "grey.100" }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedData.map((row, index) => (
                  <TableRow key={index} hover>
                    {headers.map((header) => (
                      <TableCell key={`${index}-${header}`}>{row[header] || "-"}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)} variant="contained">
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ width: "100%" }}>
          Employee data uploaded successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorAlert}
        autoHideDuration={6000}
        onClose={() => setShowErrorAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setShowErrorAlert(false)} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
    </IonPage>
 
  )
}

export default EmployeeUploadPage
